import { Fragment, type FC } from "react";
import { Flex, Icon, Text } from "@huub-web/ui";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import type {
  PasswordRule,
  PasswordRuleGroup,
} from "../../hooks/usePasswordValidation";
import { checkIconVariants } from "./PasswordRequirements.motion";
import {
  checkIconWrapperStyle,
  groupedRuleStyle,
  ruleListStyle,
  srOnlyStyle,
} from "./PasswordRequirements.css";

type PasswordRequirementsProps = {
  /**
   * Grouped rules for rendering with "N of M" semantics
   */
  ruleGroups: PasswordRuleGroup[];
  /**
   * Per-level minimum password length, used for the minLength rule interpolation
   */
  minLength: number;
};

/**
 * Renders a single password rule with a check icon and label.
 */
const RuleItem: FC<{
  rule: PasswordRule;
  minLength: number;
}> = ({ rule, minLength }) => {
  const { t } = useTranslation();

  return (
    <Flex as="li" gap="xs" align="center">
      <motion.span
        variants={checkIconVariants}
        initial={false}
        animate={rule.passed ? "checked" : "unchecked"}
        className={checkIconWrapperStyle}
      >
        <Icon
          icon="check"
          size="s"
          color={rule.passed ? "success" : "currentColor25"}
        />
      </motion.span>
      <Text variant="bodyS" color={rule.passed ? "success" : "currentColor50"}>
        {t(
          `registration.passwordRules.${rule.key}`,
          rule.key === "minLength" ? { count: minLength } : undefined,
        )}
      </Text>
      <span className={srOnlyStyle}>
        {t(
          rule.passed
            ? "registration.passwordRules.statusPassed"
            : "registration.passwordRules.statusNotPassed",
        )}
      </span>
    </Flex>
  );
};

/**
 * Renders a live-validated checklist of password requirements.
 * Supports grouped rules with "N of M" semantics (e.g. "At least 3 of the following:").
 */
export const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  ruleGroups,
  minLength,
}) => {
  const { t } = useTranslation();

  if (ruleGroups.length === 0) {
    return null;
  }

  return (
    <Flex as="ul" direction="column" gap="xs" className={ruleListStyle}>
      {ruleGroups.map((group) => {
        const isNOfM = group.requiredCount < group.rules.length;

        if (isNOfM) {
          return (
            <li key={group.rules.map((r) => r.key).join("-")}>
              <Flex gap="xs" align="center">
                <motion.span
                  variants={checkIconVariants}
                  initial={false}
                  animate={group.passed ? "checked" : "unchecked"}
                  className={checkIconWrapperStyle}
                >
                  <Icon
                    icon="check"
                    size="s"
                    color={group.passed ? "success" : "currentColor25"}
                  />
                </motion.span>
                <Text
                  variant="bodyS"
                  color={group.passed ? "success" : "currentColor50"}
                >
                  {t("registration.passwordRules.characterGroupHeader", {
                    count: group.requiredCount,
                  })}
                </Text>
                <span className={srOnlyStyle}>
                  {t(
                    group.passed
                      ? "registration.passwordRules.statusPassed"
                      : "registration.passwordRules.statusNotPassed",
                  )}
                </span>
              </Flex>
              <Flex
                as="ul"
                direction="column"
                gap="xs"
                className={`${ruleListStyle} ${groupedRuleStyle}`}
              >
                {group.rules.map((rule) => (
                  <RuleItem key={rule.key} rule={rule} minLength={minLength} />
                ))}
              </Flex>
            </li>
          );
        }

        return (
          <Fragment key={group.rules[0].key}>
            {group.rules.map((rule) => (
              <RuleItem key={rule.key} rule={rule} minLength={minLength} />
            ))}
          </Fragment>
        );
      })}
    </Flex>
  );
};
